import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  ClipboardList, Loader2, Copy, Check, X, ArrowLeft, Settings2, Lock, Unlock, Mail, Phone,
} from "lucide-react";
import { toast } from "sonner";
import { QrCode } from "lucide-react";
import { QRDialog } from "@/components/QRDialog";
import { api } from "@/lib/api";
import { RegFormDialog } from "@/components/RegFormDialog";

const STATUS = {
  pending: { label: "Offen", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  approved: { label: "Zugesagt", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  rejected: { label: "Abgesagt", cls: "bg-neutral-100 text-neutral-500 border-neutral-200" },
};

export default function Registrations() {
  const { formId } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(null);
  const [regs, setRegs] = useState([]);
  const [filter, setFilter] = useState("all");
  const [busyId, setBusyId] = useState("");
  const [editOpen, setEditOpen] = useState(false);

  const load = async () => {
    const [{ data: f }, { data: r }] = await Promise.all([
      api.get(`/reg-forms/${formId}`),
      api.get(`/reg-forms/${formId}/registrations`),
    ]);
    setForm(f);
    setRegs(r);
  };
  useEffect(() => { load().catch(() => toast.error("Formular konnte nicht geladen werden")); }, [formId]); // eslint-disable-line react-hooks/exhaustive-deps

  const publicUrl = form ? `${window.location.origin}/anmeldung/${form.slug}` : "";
  const [qrOpen, setQrOpen] = useState(false);

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(publicUrl); toast.success("Link kopiert — teilen auf Instagram, Flyern & Co."); }
    catch { toast.error("Kopieren nicht möglich — Link manuell markieren"); }
  };

  const decide = async (reg, decision) => {
    const verb = decision === "approve" ? "Zusage" : "Absage";
    if (!window.confirm(`${verb} an ${reg.name} (${reg.email}) senden?\n\nDie E-Mail geht sofort automatisch raus.`)) return;
    setBusyId(reg.id);
    try {
      await api.post(`/registrations/${reg.id}/decide`, { decision });
      toast.success(decision === "approve" ? `Zugesagt — Stand vergeben, E-Mail an ${reg.name} versendet ✅` : `Abgesagt — E-Mail an ${reg.name} versendet`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Aktion fehlgeschlagen");
    } finally {
      setBusyId("");
    }
  };

  const toggleStatus = async () => {
    const next = form.status === "open" ? "closed" : "open";
    const { data } = await api.patch(`/reg-forms/${formId}`, { status: next });
    setForm(data);
    toast.success(next === "open" ? "Anmeldung geöffnet" : "Anmeldung geschlossen");
  };

  if (!form) return <div className="py-16 flex justify-center text-neutral-400"><Loader2 className="h-6 w-6 animate-spin" /></div>;

  const filtered = filter === "all" ? regs : regs.filter((r) => r.status === filter);
  const c = form.counts || { pending: 0, approved: 0, rejected: 0 };

  return (
    <div data-testid="registrations-page" className="max-w-5xl">
      <button onClick={() => navigate("/event-radar")} className="text-[13px] text-neutral-400 hover:text-neutral-700 inline-flex items-center gap-1">
        <ArrowLeft className="h-3.5 w-3.5" /> Zurück zum Event-Radar
      </button>
      <div className="flex items-start justify-between flex-wrap gap-4 mt-2">
        <div>
          <p className="text-[11px] font-bold uppercase tracking-[0.15em] text-neutral-400 flex items-center gap-1.5">
            <ClipboardList className="h-3.5 w-3.5" /> Anmeldungen
          </p>
          <h1 className="font-head font-bold text-2xl text-neutral-900 mt-1">{form.title}</h1>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="reg-edit-form" onClick={() => setEditOpen(true)}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-700 border border-neutral-200 px-3 py-2 rounded-lg hover:bg-neutral-50">
            <Settings2 className="h-4 w-4" /> Formular
          </button>
          <button data-testid="reg-toggle-status" onClick={toggleStatus}
            className={`inline-flex items-center gap-1.5 text-sm font-medium px-3 py-2 rounded-lg border ${form.status === "open" ? "text-neutral-700 border-neutral-200 hover:bg-neutral-50" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
            {form.status === "open" ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
            {form.status === "open" ? "Schließen" : "Wieder öffnen"}
          </button>
        </div>
      </div>

      <div className="grid sm:grid-cols-3 gap-3 mt-5">
        <div className="rounded-xl bg-neutral-900 text-white p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Kontingent</div>
          <div className="font-head text-2xl font-bold mt-1" data-testid="reg-quota-counter">{c.approved} / {form.quota}</div>
          <div className="h-1.5 bg-white/15 rounded-full mt-2 overflow-hidden">
            <div className="h-full bg-emerald-400 rounded-full" style={{ width: `${Math.min(100, (c.approved / form.quota) * 100)}%` }} />
          </div>
        </div>
        <div className="rounded-xl border border-amber-200 bg-amber-50 p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-amber-600">Offen zu bearbeiten</div>
          <div className="font-head text-2xl font-bold text-amber-800 mt-1">{c.pending}</div>
        </div>
        <div className="rounded-xl border border-neutral-200 bg-white p-4">
          <div className="text-[11px] font-bold uppercase tracking-wider text-neutral-400">Öffentlicher Link</div>
          <button data-testid="reg-copy-link" onClick={copyLink} className="mt-1.5 inline-flex items-center gap-1.5 text-[13px] font-semibold text-fuchsia-700 hover:text-fuchsia-800 break-all text-left">
            <Copy className="h-3.5 w-3.5 shrink-0" /> {publicUrl.replace("https://", "")}
          </button>
          <button data-testid="reg-qr-btn" onClick={() => setQrOpen(true)} className="mt-1.5 ml-3 inline-flex items-center gap-1.5 text-[13px] font-semibold text-neutral-600 hover:text-neutral-900">
            <QrCode className="h-3.5 w-3.5 shrink-0" /> QR-Code
          </button>
          <QRDialog open={qrOpen} onClose={() => setQrOpen(false)} url={publicUrl} title={form?.title || "Anmeldung"} />
        </div>
      </div>

      <div className="flex items-center gap-1.5 mt-6">
        {[["all", `Alle (${regs.length})`], ["pending", `Offen (${c.pending})`], ["approved", `Zugesagt (${c.approved})`], ["rejected", `Abgesagt (${c.rejected})`]].map(([k, label]) => (
          <button key={k} data-testid={`reg-filter-${k}`} onClick={() => setFilter(k)}
            className={`text-[13px] font-medium px-3 py-1.5 rounded-full border transition ${filter === k ? "bg-neutral-900 text-white border-neutral-900" : "bg-white text-neutral-600 border-neutral-200 hover:bg-neutral-50"}`}>
            {label}
          </button>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {filtered.length === 0 && <p className="text-sm text-neutral-400 py-10 text-center">Noch keine Anmeldungen{filter !== "all" ? " in diesem Filter" : " — teile den öffentlichen Link"}.</p>}
        {filtered.map((r) => {
          const st = STATUS[r.status];
          return (
            <div key={r.id} data-testid={`reg-row-${r.id}`} className="bg-white border border-neutral-200 rounded-xl p-4 flex items-center gap-4 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="font-semibold text-neutral-900 text-[15px]">{r.name}</span>
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${st.cls}`}>{st.label}</span>
                  {r.stand_number && <span className="text-[11px] font-bold px-2 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-200">Stand {r.stand_number}</span>}
                  {r.stand_wish && <span className="text-[11px] text-neutral-400">{r.stand_wish}</span>}
                </div>
                <div className="flex items-center gap-3 mt-1 text-[12px] text-neutral-400 flex-wrap">
                  <span className="flex items-center gap-1"><Mail className="h-3 w-3" /> {r.email}</span>
                  {r.phone && <span className="flex items-center gap-1"><Phone className="h-3 w-3" /> {r.phone}</span>}
                  <span>{new Date(r.created_at).toLocaleString("de-DE", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}</span>
                </div>
                {r.note && <p className="text-[13px] text-neutral-600 mt-1.5 bg-neutral-50 border border-neutral-100 rounded-lg px-2.5 py-1.5">{r.note}</p>}
              </div>
              {r.status === "pending" && (
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    data-testid={`reg-approve-${r.id}`}
                    onClick={() => decide(r, "approve")}
                    disabled={busyId === r.id || c.approved >= form.quota}
                    title={c.approved >= form.quota ? "Kontingent erschöpft" : "Zusagen + E-Mail senden"}
                    className="inline-flex items-center gap-1.5 bg-emerald-600 text-white text-[13px] font-semibold px-3.5 py-2 rounded-lg hover:bg-emerald-700 disabled:opacity-40"
                  >
                    {busyId === r.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} Zusagen
                  </button>
                  <button
                    data-testid={`reg-reject-${r.id}`}
                    onClick={() => decide(r, "reject")}
                    disabled={busyId === r.id}
                    className="inline-flex items-center gap-1.5 border border-neutral-200 text-neutral-600 text-[13px] font-semibold px-3.5 py-2 rounded-lg hover:bg-rose-50 hover:text-rose-600 hover:border-rose-200 disabled:opacity-40"
                  >
                    <X className="h-4 w-4" /> Absagen
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <RegFormDialog open={editOpen} onOpenChange={setEditOpen} accountKey={form.account_key} existing={form}
        onSaved={(f) => setForm({ ...f, counts: form.counts })} />
    </div>
  );
}
